import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, Input, type OnChanges, type OnInit, type SimpleChanges, type Type, inject } from '@angular/core';
import { BehaviorSubject, type Observable, type OperatorFunction, zip } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { DeveloperHelperComponent } from '../../../components/developer-helper/developer-helper';
import type { CustomInput } from '../../../interfaces/CustomInput.interface';
import type { RegularObject } from '../../../interfaces/utils.interface';
import { CustomInputProvider } from '../../custom-input.provider';
import type { DataSource } from '../../data-source.class';
import { DataSourceProvider } from '../../data-source.provider';
import { DataVizWidgetSpawnerComponent } from '../data-viz-widget-spawner/data-viz-widget-spawner';
import type { DataVizWidgetConfig } from '../data-viz-widget.interface';
import { getGhostComponent, getShellComponent } from '../data-viz-widget.utils';
import type { WidgetGhost } from '../ghosts/widget-ghost.class';
import type { WidgetGhostConfig } from '../ghosts/widget-ghost.interface';
import type { WidgetShell } from '../shells/widget-shell.class';
import type { WidgetShellConfig } from '../shells/widget-shell.interface';
import { OldDataSourceCode } from './../../../services/developer-helper.service';

@Component({
    styleUrls: ['./data-viz-widget-auto-spawner.scss'],
    selector: 'data-viz-widget-auto-spawner',
    templateUrl: 'data-viz-widget-auto-spawner.html',
    imports: [DeveloperHelperComponent, DataVizWidgetSpawnerComponent, AsyncPipe]
})
export class DataVizWidgetAutoSpawnerComponent implements OnChanges, OnInit {
    private _customInputsProvider = inject(CustomInputProvider);
    private _dataSourceProvider = inject(DataSourceProvider);

    @Input() widgetConfig!: DataVizWidgetConfig;
    @Input() dataSources!: RegularObject<DataSource<any>>;

    private _widgetConfig$ = new BehaviorSubject<DataVizWidgetConfig | null>(null);

    ghostComponent$!: Observable<Type<WidgetGhost<any, any>>>;
    ghostConfig$!: Observable<WidgetGhostConfig>;

    shellComponent$!: Observable<Type<WidgetShell<any>>>;
    shellConfig$!: Observable<WidgetShellConfig>;

    customInputs$!: Observable<CustomInput[]>;

    OldDataSourceCode = OldDataSourceCode;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.widgetConfig) {
            this._widgetConfig$.next(changes.widgetConfig.currentValue);
        }
    }

    ngOnInit() {
        this.customInputs$ = this._widgetConfig$.pipe(
            filter(isNotNullish),
            map(config => (config.customInputs ? this._customInputsProvider.createCustomInputs(config.customInputs) : [])),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.shellComponent$ = this._widgetConfig$.pipe(
            filter(isNotNullish),

            map(config => getShellComponent(config.shell.type)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.shellConfig$ = zip(this._widgetConfig$, this.customInputs$).pipe(
            switchMap(([config, customInputs]) => {
                return this._dataSourceProvider.applyCustomInputsToObject$(config.shell.config, customInputs);
            }) as OperatorFunction<any, WidgetShellConfig>,
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.ghostComponent$ = this._widgetConfig$.pipe(
            filter(isNotNullish),

            map(config => getGhostComponent(config.ghost.type)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.ghostConfig$ = zip(this._widgetConfig$, this.customInputs$).pipe(
            filter(([config]) => isNotNullish(config)),
            switchMap(([config, customInputs]) => {
                return this._dataSourceProvider.applyCustomInputsToObject$(config!.ghost.config, customInputs);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }
}
